import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';
import { NotifcationService } from 'src/graphql/services/notification.service';
import { Notification, NotificationStatus } from 'src/graphql/generated/schema';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { HOME_UNREAD_CARD_LIMIT } from 'src/app/shared/consts/notifications';
import { COLLAPSE_TIMING, EXPAND_TIMING } from 'src/app/shared/animations/expand-fade.animation';

@Component({
  selector: 'alerts-widget',
  templateUrl: './alerts-widget.component.html',
  styleUrls: ['./alerts-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // A ticked card leaves for the archive and the next unread takes its place; the
  // last one being ticked gives up its space gradually rather than snapping.
  animations: [
    // Gives up the cards' vertical space over COLLAPSE_TIMING instead of all at
    // once, so the panel shrinks to its sign-off row smoothly. No :enter step -
    // the container arrives with the panel, and the cards inside it have their own.
    trigger('cardsCollapse', [
      transition(':leave', [
        // overflow, or the cards spill out of the shrinking box. It does not
        // interpolate, so it is a starting style rather than something animated.
        style({ overflow: 'hidden' }),
        // marginTop as well as height: Angular does not animate margins for you,
        // so the container's 15px would hold its full value to the last frame and
        // leave a gap under the panel header as everything above it shrank.
        animate(COLLAPSE_TIMING, style({ height: 0, marginTop: 0, opacity: 0 })),
      ]),
    ]),
    trigger('cardSwap', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        // Shared timings, per expand-fade.animation.ts: "never re-declare a
        // duration or curve in a component". Also gives reduced-motion support,
        // which the hardcoded values silently skipped.
        animate(EXPAND_TIMING, style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      // No :leave step, deliberately. A ticked card must not hold its slot while
      // it fades - that is what made the incoming card render below it and then
      // jump up. Taking it out of layout with position:absolute fixed that at a
      // cap of one card, where the vacated slot was the only slot; at the cap of
      // two this widget now renders, the absolute card keeps its static position
      // while the surviving card reflows into that same slot, so the two draw on
      // top of each other for the length of the fade. Removing the card outright
      // is what both caps want: no slot is held, the survivor slides up, and the
      // new card fades in via :enter.
    ]),
  ],
})
export class AlertsWidgetComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  public unread: Notification[] = [];

  constructor(
    private notificationService: NotifcationService,
    private changeRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
  ) {}

  public ngOnInit(): void {
    this.notificationService.unread$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(unread => {
        this.unread = unread;
        this.changeRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public get unreadCount(): number {
    return this.unread.length;
  }

  /**
   * Just the number, shown in accent beside the title. The panel opens
   * collapsed, so this is the only unread signal until the resident expands it.
   */
  public get unreadNote(): string {
    return this.unreadCount ? `${this.unreadCount}` : '';
  }

  /** At most HOME_UNREAD_CARD_LIMIT cards: the most severe unread messages. */
  public get visibleCards(): Notification[] {
    return this.unread.slice(0, HOME_UNREAD_CARD_LIMIT);
  }

  /**
   * Track by id, not object identity: applyReadStatus rebuilds the objects, so
   * without this the cards would be torn down and rebuilt on every emission and
   * the swap animation could never tell "same card" from "next card" apart.
   */
  public trackAlert(_index: number, alert: Notification): string {
    return alert.id;
  }

  public get showMarkAll(): boolean {
    return this.unreadCount > 1;
  }

  /**
   * Ticking a card marks it read; it then drops out of unread$ and the next
   * unread animates into the vacated slot. Read messages live in the archive,
   * not here, so the panel never expands to hold both.
   */
  public updateNotification(notification: Notification): void {
    const newStatus = notification.status === NotificationStatus.Read
      ? NotificationStatus.NotRead
      : NotificationStatus.Read;

    this.analyticsService.trackWidgetInteraction(
      'alerts',
      newStatus === NotificationStatus.Read ? 'mark_read' : 'mark_unread',
    );

    this.notificationService.setRead(notification, newStatus);
  }

  /** No confirmation and no toast: the panel leaving is the confirmation. */
  public async markAllRead(): Promise<void> {
    this.analyticsService.trackWidgetInteraction('alerts', 'mark_all_read');
    await this.notificationService.markAllRead();
  }
}
